import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import Icon from '../../../components/AppIcon';

const emissionsData = [
  { month: 'Jul', nox: 42, sox: 28, pm: 15 },
  { month: 'Aug', nox: 45, sox: 30, pm: 14 },
  { month: 'Sep', nox: 38, sox: 25, pm: 16 },
  { month: 'Oct', nox: 50, sox: 32, pm: 18 },
  { month: 'Nov', nox: 47, sox: 29, pm: 13 },
  { month: 'Dec', nox: 44, sox: 27, pm: 12 },
  { month: 'Jan', nox: 41, sox: 24, pm: 11 },
];

const wasteData = [
  { name: 'Disposed', value: 68, color: '#10b981' },
  { name: 'Pending Disposal', value: 22, color: '#f59e0b' },
  { name: 'In Storage', value: 10, color: '#ef4444' },
];

const medicalCards = [
  { label: 'Critical Medical Cases', value: 1, icon: 'HeartPulse', rag: 'red', sub: 'ICU monitoring' },
  { label: 'First Aid Cases (MTD)', value: 7, icon: 'Cross', rag: 'amber', sub: '3 recurring' },
  { label: 'Occupational Illness', value: 2, icon: 'Stethoscope', rag: 'amber', sub: 'Under review' },
  { label: 'Spill Incidents (MTD)', value: 0, icon: 'Droplets', rag: 'green', sub: 'No incidents' },
];

const ragMap = {
  green: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', badge: 'bg-emerald-500/20 text-emerald-400' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-500', badge: 'bg-amber-500/20 text-amber-400' },
  red: { bg: 'bg-red-500/10', text: 'text-red-500', badge: 'bg-red-500/20 text-red-400' },
};

const EnvironmentalMedicalSection = () => {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-1 h-6 bg-teal-500 rounded-full" />
        <h2 className="text-base font-semibold text-foreground">Environmental & Medical</h2>
        <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">Last 7 months</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Emissions Trend */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Emissions Trend (mg/m³)</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={emissionsData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
              <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="nox" stroke="#f97316" strokeWidth={2} dot={false} name="NOx" />
              <Line type="monotone" dataKey="sox" stroke="#8b5cf6" strokeWidth={2} dot={false} name="SOx" />
              <Line type="monotone" dataKey="pm" stroke="#06b6d4" strokeWidth={2} dot={false} name="PM" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {/* Medical Cards */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Medical Status</p>
          <div className="grid grid-cols-2 gap-2">
            {medicalCards?.map((card) => {
              const rag = ragMap?.[card?.rag];
              return (
                <div key={card?.label} className={`${rag?.bg} border border-border rounded-lg p-3 flex flex-col gap-1`}>
                  <Icon name={card?.icon} size={14} className={rag?.text} />
                  <p className="text-xl font-bold text-foreground">{card?.value}</p>
                  <p className="text-xs font-medium text-foreground leading-tight">{card?.label}</p>
                  <p className="text-xs text-muted-foreground">{card?.sub}</p>
                </div>
              );
            })}
          </div>
        </div>
        {/* Waste Pie */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide">Hazardous Waste (MTD)</p>
          <div className="flex flex-col items-center gap-3">
            <ResponsiveContainer width="100%" height={150}>
              <PieChart>
                <Pie data={wasteData} cx="50%" cy="50%" outerRadius={65} dataKey="value" labelLine={false}>
                  {wasteData?.map((entry, i) => <Cell key={i} fill={entry?.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-col gap-1.5 w-full">
              {wasteData?.map((d) => (
                <div key={d?.name} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: d?.color }} />
                  <span className="text-xs text-muted-foreground flex-1">{d?.name}</span>
                  <span className="text-xs font-semibold text-foreground">{d?.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalMedicalSection;